import * as React from "react";
import {createPortal} from "react-dom";
import {Box} from "@mui/material";
import {alpha} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";

export interface PointsEarnedFloaterProps {
	/** Points value shown as "+{points} POINTS". */
	points: number;
	/** Total animation duration in milliseconds. */
	durationMs?: number;
	/** Called once when the float-out finishes (use to unmount). */
	onAnimationEnd?: () => void;
}

const DEFAULT_DURATION_MS = 4000;

/** Root overlay: above MUI layers and in-app chrome that uses high z-index (e.g. ~9999). */
const FLOATER_ROOT_Z_INDEX = 11_000;

const useStyles = makeStyles<{durationMs: number}>()((theme, {durationMs}) => {
	const glowStrong = alpha(theme.palette.primary.main, 0.6);
	const glowSoft = alpha(theme.palette.warning.main, 0.35);
	const ease = `${durationMs}ms ease-out`;

	return {
		root: {
			position: "fixed",
			inset: 0,
			pointerEvents: "none",
			zIndex: FLOATER_ROOT_Z_INDEX,
			overflow: "hidden"
		},
		pill: {
			position: "absolute",
			left: "50%",
			top: "50%",
			transform: "translate(-50%, -50%)",
			opacity: 1,
			transition: `transform ${ease}, opacity ${ease}, box-shadow ${ease}`,
			padding: `${theme.spacing(1)} ${theme.spacing(2.5)}`,
			borderRadius: theme.spacing(12.5),
			background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.primary.main})`,
			boxShadow: `0 0 ${theme.spacing(2.5)} ${glowStrong}, 0 0 ${theme.spacing(4)} ${glowSoft}`,
			fontFamily: theme.typography.fontFamily,
			fontWeight: theme.typography.fontWeightBold,
			fontSize: theme.typography.subtitle1.fontSize,
			lineHeight: theme.typography.subtitle1.lineHeight,
			letterSpacing: theme.spacing(0.0625),
			color: theme.palette.common.black,
			textTransform: "uppercase",
			whiteSpace: "nowrap",
			fontVariantNumeric: "tabular-nums"
		},
		pillExit: {
			transform: "translate(-50%, calc(-50% - 42vh))",
			opacity: 0,
			boxShadow: `0 0 ${theme.spacing(0.5)} ${alpha(theme.palette.primary.main, 0)}`
		}
	};
});

export const PointsEarnedFloater: React.FC<PointsEarnedFloaterProps> = ({
	points,
	durationMs = DEFAULT_DURATION_MS,
	onAnimationEnd
}) => {
	const {classes, cx} = useStyles({durationMs});
	const {t} = useSSTranslation();
	const [exitPhase, setExitPhase] = React.useState(false);
	const completedRef = React.useRef(false);

	const finish = React.useCallback(() => {
		if (completedRef.current) {
			return;
		}
		completedRef.current = true;
		onAnimationEnd?.();
	}, [onAnimationEnd]);

	React.useLayoutEffect(() => {
		let innerId = 0;
		const outerId = requestAnimationFrame(() => {
			innerId = requestAnimationFrame(() => {
				setExitPhase(true);
			});
		});
		return () => {
			cancelAnimationFrame(outerId);
			if (innerId !== 0) {
				cancelAnimationFrame(innerId);
			}
		};
	}, []);

	React.useEffect(() => {
		if (!exitPhase) {
			return;
		}
		const id = window.setTimeout(() => {
			finish();
		}, durationMs + 150);
		return () => window.clearTimeout(id);
	}, [exitPhase, durationMs, finish]);

	const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
		if (event.propertyName !== "opacity" && event.propertyName !== "transform") {
			return;
		}
		finish();
	};

	const suffix = t("points_earned_suffix", "POINTS");

	const content = (
		<Box className={classes.root} role="presentation">
			<Box
				className={cx(classes.pill, exitPhase && classes.pillExit)}
				onTransitionEnd={handleTransitionEnd}
				role="status"
				aria-live="polite"
				data-test-id="points-earned-floater"
			>
				{`+${points} ${suffix}`}
			</Box>
		</Box>
	);

	if (typeof document === "undefined") {
		return null;
	}

	return createPortal(content, document.body);
};
